import type { ComponentProps } from 'react';
import { PortIconSVG } from 'atto-svgs';

import { colors, darkThemeSelector, keyframes, styled } from '../../stitches.config';

export type TemperatureScale = 'celsius' | 'fahrenheit';

export type TemperatureHeat = 'cold' | 'normal' | 'warm' | 'hot';

/** A model's rated operating range, in degrees Celsius. */
export type TemperatureRange = {
  lowest: number;
  highest: number;
};

const coolTint = {
  color: colors.hardwareStrokeAlternativeLight,

  [darkThemeSelector]: {
    color: colors.hardwareStrokeAlternativeDark,
  },
};

const TemperatureContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '$24',
  height: '$20',

  variants: {
    heat: {
      cold: coolTint,
      normal: coolTint,
      warm: {
        color: colors.hardwareStrokeAttentionLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeAttentionDark,
        },
      },
      hot: {
        color: colors.hardwareStrokeNegativeLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeNegativeDark,
        },
      },
    },
  },

  defaultVariants: {
    heat: 'normal',
  },
});
TemperatureContainer.displayName = 'TemperatureContainer';

const TemperatureFrame = styled('div', {
  position: 'relative',
  display: 'flex',

  variants: {
    neutral: {
      true: {
        color: colors.hardwareStrokeNeutralLight,

        [darkThemeSelector]: {
          color: colors.hardwareStrokeNeutralDark,
        },
      },
      false: {},
    },
  },
});
TemperatureFrame.displayName = 'TemperatureFrame';

const glowPulse = keyframes({
  from: { opacity: 0.25 },
  to: { opacity: 0.6 },
});

// A shadowed copy of the tube, so the glow traces its outline. Only the copy's opacity animates, so
// the shadow is drawn once and the pulse stays on the compositor.
const TemperatureGlow = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  filter: 'drop-shadow(0 0 1px currentColor) drop-shadow(0 0 2.5px currentColor)',
  animation: `${glowPulse} 1.2s ease-in-out infinite alternate`,

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 0.4,
  },
});
TemperatureGlow.displayName = 'TemperatureGlow';

const TemperatureLayer = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
TemperatureLayer.displayName = 'TemperatureLayer';

const TemperatureMercury = styled(TemperatureLayer, {
  transition: 'clip-path 600ms ease-out',

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
});
TemperatureMercury.displayName = 'TemperatureMercury';

// Rows of the 24×20 art: the bulb fills from 14 down, the column runs up to 4.
const MERCURY_EMPTY_Y = 14;
const MERCURY_FULL_Y = 4;
const ART_HEIGHT = 20;

/** How far up its rated range a reading sits, from 0 to 1. Without a range the column is full. */
export function getTemperatureLevel(celsius: number, range?: TemperatureRange): number {
  if (!range || range.highest <= range.lowest) return 1;
  return Math.min(Math.max((celsius - range.lowest) / (range.highest - range.lowest), 0), 1);
}

/**
 * Tiers a reading against the model's rated range. `hot` starts at 95% of the high, where the
 * temperature alert fires; `warm` at 80%. Without a range there is nothing to tier against.
 */
export function getTemperatureHeat(celsius: number, range?: TemperatureRange): TemperatureHeat {
  if (!range) return 'normal';
  if (celsius < range.lowest) return 'cold';
  if (celsius >= range.highest * 0.95) return 'hot';
  if (celsius >= range.highest * 0.8) return 'warm';
  return 'normal';
}

const temperatureHeatLabels: Record<TemperatureHeat, string | null> = {
  cold: 'Below operating range',
  normal: null,
  warm: 'Running warm',
  hot: 'Running hot',
};

const toScale = (celsius: number, scale: TemperatureScale) =>
  scale === 'fahrenheit' ? (celsius * 9) / 5 + 32 : celsius;

const scaleSymbols: Record<TemperatureScale, string> = {
  celsius: '°C',
  fahrenheit: '°F',
};

const wholeDegrees = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const tenthDegrees = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

export function formatTemperature(celsius: number, scale: TemperatureScale = 'celsius'): string {
  return `${tenthDegrees.format(toScale(celsius, scale))} ${scaleSymbols[scale]}`;
}

/** Whole degrees with no unit ('47°'), short enough for a slot's number chip. */
export function formatTemperatureShort(
  celsius: number,
  scale: TemperatureScale = 'celsius',
): string {
  return `${wholeDegrees.format(toScale(celsius, scale))}°`;
}

type DescribeTemperatureOptions = {
  heat?: TemperatureHeat;
  name?: string;
  scale?: TemperatureScale;
};

/** The full reading as words ('CPU · 61.8 °C · Running hot'), for a tooltip or accessible name. */
export function describeTemperature(
  celsius: number,
  { heat = 'normal', name, scale = 'celsius' }: DescribeTemperatureOptions = {},
): string {
  return [name, formatTemperature(celsius, scale), temperatureHeatLabels[heat]]
    .filter(Boolean)
    .join(' · ');
}

export type TemperatureProps = Omit<ComponentProps<typeof TemperatureContainer>, 'heat'> & {
  celsius: number;
  /**
   * How hot the reading runs for its model, which tints the mercury and tick marks. A `hot` reading
   * tints the whole glyph and pulses a glow behind it. See `getTemperatureHeat`.
   */
  heat?: TemperatureHeat;
  /** Accessible name. Defaults to `describeTemperature`'s reading. */
  label?: string;
  /** How high the mercury stands, from 0 to 1. See `getTemperatureLevel`. */
  level?: number;
  /** Drops a `hot` glyph's glow, for a slot a filter has dimmed. */
  muted?: boolean;
  /** What the sensor measures, leading the default accessible name. */
  name?: string;
  scale?: TemperatureScale;
};

/**
 * A thermal sensor as a faceplate glyph, its mercury tinted by `heat` and standing at `level`. Its
 * reading is the accessible name.
 */
export function Temperature({
  celsius,
  heat = 'normal',
  label,
  level = 1,
  muted = false,
  name,
  scale = 'celsius',
  ...remaining
}: TemperatureProps) {
  const mercuryTop = MERCURY_EMPTY_Y - level * (MERCURY_EMPTY_Y - MERCURY_FULL_Y);

  return (
    <TemperatureContainer
      {...remaining}
      role="img"
      aria-label={label ?? describeTemperature(celsius, { heat, name, scale })}
      heat={heat}
    >
      {heat === 'hot' && !muted && (
        <TemperatureGlow>
          <PortIconSVG.Thermometer aria-hidden />
        </TemperatureGlow>
      )}
      <TemperatureFrame neutral={heat !== 'hot'}>
        <PortIconSVG.Thermometer aria-hidden />
      </TemperatureFrame>
      <TemperatureLayer>
        <PortIconSVG.ThermometerTicks aria-hidden />
      </TemperatureLayer>
      <TemperatureMercury style={{ clipPath: `inset(${(mercuryTop / ART_HEIGHT) * 100}% 0 0 0)` }}>
        <PortIconSVG.ThermometerMercury aria-hidden />
      </TemperatureMercury>
    </TemperatureContainer>
  );
}
